import { create } from "zustand";
import { persist } from "zustand/middleware";
import { callApi } from "@/hooks/useFetch";
import type {
  TourCompletionListResponse,
  CompleteTourResponse,
} from "@/types/tour";

interface TourState {
  completedTourIds: string[];
  isSynced: boolean;
  activeTourId: string | null;
}

interface TourActions {
  syncCompletions: () => Promise<void>;
  markCompleted: (tourId: string) => Promise<void>;
  setActiveTour: (tourId: string | null) => void;
  isTourCompleted: (tourId: string) => boolean;
  reset: () => void;
}

interface TourStore extends TourState, TourActions {}

const initialState: TourState = {
  completedTourIds: [],
  isSynced: false,
  activeTourId: null,
};

export const useTourStore = create<TourStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      syncCompletions: async () => {
        const data = await callApi<TourCompletionListResponse>(
          "/tours/completions"
        );
        set({
          completedTourIds: data.completions.map((c) => c.tour_id),
          isSynced: true,
        });
      },

      markCompleted: async (tourId: string) => {
        const current = get().completedTourIds;
        if (current.includes(tourId)) return;

        set({ completedTourIds: [...current, tourId] });

        await callApi<CompleteTourResponse>("/tours/completions", {
          method: "POST",
          data: { tour_id: tourId },
        });
      },

      setActiveTour: (tourId: string | null) => set({ activeTourId: tourId }),

      isTourCompleted: (tourId: string) =>
        get().completedTourIds.includes(tourId),

      reset: () => set(initialState),
    }),
    {
      name: "tour-storage",
      partialize: (state) => ({
        completedTourIds: state.completedTourIds,
      }),
    }
  )
);
